import React, { useState, useEffect, useRef } from 'react';
import { Image, Skeleton, Text } from '@mantine/core';
import { getImageFile } from 'utils/helper';

const imageCache = new Map(); // src -> { url, expiry }
const inflight = new Map(); // src -> Promise<objectUrl>, dedupes concurrent requests
const CACHE_DURATION = 60 * 60 * 1000;
const FETCH_TIMEOUT = 30 * 1000;

const getCacheItem = (key) => {
    const item = imageCache.get(key);
    if (!item) return null;
    if (Date.now() > item.expiry) {
        URL.revokeObjectURL(item.url);
        imageCache.delete(key);
        return null;
    }
    return item.url;
};

const setCacheItem = (key, url) => {
    imageCache.set(key, { url, expiry: Date.now() + CACHE_DURATION });
};

// Periodically revoke and remove expired cache entries (single module-level sweep)
setInterval(() => {
    for (const [key, item] of imageCache.entries()) {
        if (Date.now() > item.expiry) {
            URL.revokeObjectURL(item.url);
            imageCache.delete(key);
        }
    }
}, 10 * 60 * 1000);

// Concurrency-limited fetch queue — max 5 simultaneous image requests
const MAX_CONCURRENT = 5;
let activeCount = 0;
const queue = [];

const processQueue = () => {
    while (activeCount < MAX_CONCURRENT && queue.length > 0) {
        const task = queue.shift();
        activeCount++;
        task().finally(() => {
            activeCount--;
            processQueue();
        });
    }
};

const runQueued = (task) =>
    new Promise((resolve, reject) => {
        queue.push(() => task().then(resolve, reject));
        processQueue();
    });

// Resolves with an object URL. Dedupes by src, rejects on HTTP errors
// (never caches failures), and aborts after FETCH_TIMEOUT so a hung
// request can't hold a queue slot forever.
const loadImage = (src) => {
    const cached = getCacheItem(src);
    if (cached) return Promise.resolve(cached);
    if (inflight.has(src)) return inflight.get(src);

    const promise = runQueued(() => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
        return getImageFile(src, controller.signal)
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                setCacheItem(src, url);
                return url;
            })
            .finally(() => clearTimeout(timer));
    }).finally(() => inflight.delete(src));

    inflight.set(src, promise);
    return promise;
};

const ResolverImage = React.memo(({
    src = '',
    onLoaded,
    width = '100%',
    height = '100%',
    classes = {},
    alt = '',
    radius = 0,
    fit = 'cover',
    eager = false,
}) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = useState(eager);
    const [imageState, setImageState] = useState(() => {
        const cached = getCacheItem(src);
        return { url: cached || '', isLoading: !!src && !cached, error: null };
    });

    useEffect(() => {
        if (eager || isVisible) return;
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { rootMargin: '300px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [eager, isVisible]);

    useEffect(() => {
        if (!src) {
            setImageState({ url: '', isLoading: false, error: null });
            return;
        }
        if (!isVisible) return;

        const cached = getCacheItem(src);
        if (cached) {
            setImageState({ url: cached, isLoading: false, error: null });
            if (onLoaded) onLoaded(cached);
            return;
        }

        let cancelled = false;
        setImageState({ url: '', isLoading: true, error: null });

        loadImage(src)
            .then((url) => {
                if (cancelled) return;
                setImageState({ url, isLoading: false, error: null });
                if (onLoaded) onLoaded(url);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error loading image:', error, { src });
                setImageState({ url: '', isLoading: false, error: 'Failed to load image' });
            });

        return () => {
            cancelled = true;
        };
    }, [src, isVisible]);

    return (
        <div ref={containerRef} style={{
            width: width === 'auto' ? '100%' : width,
            height,
            transition: 'opacity 0.2s ease-in-out',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            {imageState.isLoading ? (
                <Skeleton width={width === 'auto' ? '100%' : width} height={height} />
            ) : imageState.error ? (
                <div>
                    <Text size='sm' color='dimmed'>No image</Text>
                </div>
            ) : (
                <Image
                    fit={fit}
                    className={classes}
                    src={imageState.url}
                    alt={alt}
                    radius={radius}
                    height={height}
                    width={width}
                    withPlaceholder
                    style={{ opacity: imageState.url ? 1 : 0 }}
                />
            )}
        </div>
    );
});

ResolverImage.displayName = 'ResolverImage';

export default ResolverImage;
